import React, { useState, useEffect } from "react";
import axios from "axios";
import RoomServiceIcon from "../../icons/rs.png";
import { Sparkles } from "lucide-react";
import { baseUrl } from "../../Environments/environment";
import { toast, ToastContainer, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import HouseKeepingRequestAddOn from "../Services/HouseKeepingRequestAddOn";
import { useParams } from "react-router-dom";

const HouseKeeping = ({
	onMoreServicesClick,
	onBack,
	isRegistered,
	guestSessionId,
	roomno,
}) => {
	const [isRequesting, setIsRequesting] = useState(false);
	const [isRequested, setIsRequested] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [houseKeeping, setHouseKeeping] = useState({
		name: "",
		description: "",
		disabled: false,
		options: [],
	});
	const [selectedOptions, setSelectedOptions] = useState([]);
	const [houseKeepingRequests, setHouseKeepingRequests] = useState([]);
	const { propertyId } = useParams();

	// Check if there's a pending request with status "Requested"
	const hasPendingRequest = houseKeepingRequests.some(
		(request) => request.requestStatus === "Requested",
	);

	// Fetch housekeeping data from API
	const getHouseKeeping = async () => {
		if (!propertyId) return;

		try {
			if (guestSessionId) {
				const response = await axios.get(
					`${baseUrl}/guestApi/${guestSessionId}/houseKeepingRequest`,
				);
				console.log("Response", response.data.result);
				setHouseKeeping(response.data.result);
				if (response.data.result.disabled) {
					toast.error("Housekeeping service is currently disabled");
				}
			} else {
				const response = await axios.get(
					`${baseUrl}/houseKeepingv2/${propertyId}/houseKeepingData`,
				);
				setHouseKeeping(response.data.result);
				if (response.data.result.disabled) {
					toast.error("Housekeeping service is currently disabled");
				}
			}
		} catch (error) {
			console.error("Error getting Housekeeping data:", error);
			toast.error("Failed to load housekeeping service");
		}
	};

	// Fetch existing housekeeping requests
	const getHouseKeepingRequests = async () => {
		try {
			let response;
			if (guestSessionId) {
				// If guestSessionId is available, use guest API endpoint
				response = await axios.get(
					`${baseUrl}/guestApi/${guestSessionId}/houseKeepingRequest`,
				);
			} else if (propertyId && roomno) {
				// Otherwise use property/room endpoint
				response = await axios.get(
					`${baseUrl}/houseKeepingv2/${propertyId}/houseKeepingRequests/${roomno}`,
				);
			} else {
				return;
			}

			console.log("Housekeeping Requests Response:", response.data.result);
			setHouseKeepingRequests(response.data.result || []);
		} catch (error) {
			console.error("Error getting House Keeping Requests:", error);
		}
	};

	useEffect(() => {
		if (propertyId) {
			getHouseKeeping();
		}
	}, [propertyId]);

	useEffect(() => {
		getHouseKeepingRequests();
	}, [propertyId, roomno, guestSessionId]);

	const handleOptionChange = (e) => {
		if (e.target.checked) {
			setSelectedOptions([...selectedOptions, e.target.value]);
		} else {
			setSelectedOptions(
				selectedOptions.filter((option) => option !== e.target.value),
			);
		}
	};

	const handleSendRequest = async () => {
		// Validate options if options are available
		if (houseKeeping.options.length !== 0 && selectedOptions.length === 0) {
			toast.error("Please select at least one option");
			setIsRequesting(false);
			setIsRequested(false);
			return;
		}

		setIsRequesting(true);

		if (guestSessionId) {
			try {
				console.log("Guest Session Id", guestSessionId);
				const data = await axios.post(
					`${baseUrl}/guestApi/${guestSessionId}/houseKeepingRequest`,
					{
						name: houseKeeping.name,
						description: houseKeeping.description,
						options: selectedOptions,
					},
				);
				if (data.status === 200) {
					toast.success("Request sent successfully");
					setIsRequested(true);
					setSelectedOptions([]);
					setIsModalOpen(false);
					console.log(data);
					// Refresh requests list
					await getHouseKeepingRequests();
					return;
				}
			} catch (error) {
				toast.error(error.response?.data?.message || "Failed to send request");
				console.log(error);
				return;
			} finally {
				setIsRequesting(false);
			}
		} else {
			try {
				const data = await axios.post(
					`${baseUrl}/houseKeepingv2/${propertyId}/houseKeepingRequest`,
					{
						name: houseKeeping.name,
						description: houseKeeping.description,
						options: selectedOptions,
						requestType: "houseKeeping",
						type: "houseKeeping",
						roomNumber: roomno,
					},
				);
				if (data.status === 200) {
					toast.success("Request sent successfully");
					setIsRequested(true);
					setSelectedOptions([]);
					setIsModalOpen(false);
					console.log(data);
					// Refresh requests list
					await getHouseKeepingRequests();
					// Reset after 3 seconds
					setTimeout(() => {
						setIsRequested(false);
					}, 3000);
				}
				console.log("Name : ", houseKeeping.name);
				console.log("Description : ", houseKeeping.description);
				console.log("Options : ", selectedOptions);
			} catch (e) {
				toast.error(e.response?.data?.message || "Failed to send request");
				console.log(e);
			} finally {
				setIsRequesting(false);
			}
		}
	};

	const handleRequestHousekeeping = async () => {
		// Open modal to show options
		setIsModalOpen(true);
	};

	return (
		<div className="min-h-screen flex flex-col px-5 py-10 bg-gray-900">
			<div className="flex flex-col items-center justify-center w-full max-w-md mx-auto flex-1">
				{/* Header */}
				<div className="text-center mb-6">
					<h1 className="text-3xl font-bold text-white font-poppins mb-2">
						Housekeeping Service
					</h1>
					<p className="text-neutral-300 font-poppins">
						Request housekeeping service for your room
					</p>
				</div>

				{/* Circular Logo Container */}
				<div className="flex flex-col items-center justify-center mb-6">
					{/* Main circular container - ensure it's not clipped */}
					<div
						className="relative mb-8 flex items-center justify-center"
						style={{ minWidth: "280px", minHeight: "280px" }}
					>
						<div
							className={`rounded-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 shadow-2xl flex items-center justify-center transition-all duration-500 ${
								isRequesting
									? "animate-bounce"
									: "hover:scale-105 hover:shadow-indigo-500/50"
							}`}
							style={{ width: "256px", height: "256px" }}
						>
							{/* Circular white background container */}
							<div className="w-[90%] h-[90%] rounded-full bg-white flex items-center justify-center shadow-inner overflow-hidden">
								{/* Image container with proper sizing */}
								<div className="w-full h-full flex items-center justify-center rounded-full">
									<img
										src={RoomServiceIcon}
										alt="Housekeeping"
										className={`object-contain transition-transform duration-500 ${
											isRequesting
												? "animate-pulse rotate-12"
												: "hover:scale-110"
										}`}
										style={{
											width: "200%",
											height: "200%",
											objectFit: "contain",
										}}
									/>
								</div>
							</div>

							{/* Sparkle effect on hover */}
							<div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
								<Sparkles className="w-6 h-6 text-yellow-300 animate-spin" />
							</div>
						</div>
					</div>

					{/* Request Button */}
					<button
						onClick={handleRequestHousekeeping}
						disabled={houseKeeping.disabled || isRequesting || isRequested}
						className={`w-full max-w-xs px-8 py-4 text-white rounded-md font-poppins font-semibold text-lg transition-all duration-300 ${
							isRequested
								? "bg-green-500 shadow-lg"
								: isRequesting || houseKeeping.disabled
									? "bg-indigo-400 cursor-not-allowed"
									: "bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
						} disabled:opacity-70 disabled:cursor-not-allowed`}
					>
						{/* Button content with animation */}
						<span className="relative z-10 flex items-center justify-center gap-2">
							{isRequesting ? (
								<>
									<svg
										className="animate-spin h-5 w-5 text-white"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										></circle>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										></path>
									</svg>
									Requesting...
								</>
							) : isRequested ? (
								<>
									<svg
										className="w-5 h-5 animate-bounce"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M5 13l4 4L19 7"
										/>
									</svg>
									Request Sent!
								</>
							) : (
								<>
									<Sparkles className="w-5 h-5" />
									{houseKeepingRequests.length === 0
										? "Request Housekeeping"
										: "Send New Request"}
								</>
							)}
						</span>
					</button>

					{/* Success message */}
					{isRequested && (
						<div className="mt-4 text-center transform transition-all duration-500 ease-out animate-pulse">
							<p className="text-green-400 font-poppins font-medium">
								✓ Your request has been submitted successfully!
							</p>
						</div>
					)}

					{/* Pending request message */}
					{hasPendingRequest && !isRequested && (
						<div className="mt-4 text-center transform transition-all duration-500 ease-out">
							<p className="text-green-400 font-poppins font-medium">
								✓ Your request has already been submitted!
							</p>
						</div>
					)}
				</div>

				{/* More Services Section */}
				<div className="mt-24 w-full max-w-md">
					<button
						onClick={onMoreServicesClick}
						className="w-full px-6 py-3 bg-indigo-600 text-white rounded-md font-poppins font-semibold text-base hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-300 flex items-center justify-center gap-2"
					>
						<svg
							className="w-5 h-5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M12 6v6m0 0v6m0-6h6m-6 0H6"
							/>
						</svg>
						More Services
					</button>
				</div>
			</div>

			{/* Modal for Options */}
			{isModalOpen && (
				<div className="fixed inset-0 bg-gray-700 bg-opacity-70 z-40">
					<HouseKeepingRequestAddOn
						houseKeeping={houseKeeping}
						isModalOpen={isModalOpen}
						setIsModalOpen={setIsModalOpen}
						handleSendRequest={handleSendRequest}
						handleOptionChange={handleOptionChange}
						selectedOptions={selectedOptions}
					/>
				</div>
			)}

			<ToastContainer
				position="top-right"
				autoClose={5000}
				hideProgressBar
				newestOnTop={false}
				closeOnClick
				rtl={false}
				pauseOnFocusLoss
				draggable
				pauseOnHover
				theme="light"
				transition={Bounce}
			/>
		</div>
	);
};

export default HouseKeeping;
